import { ApolloError, NetworkStatus } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import {
  AthletesAndAnthropometryEntriesCursorConnectionQuery,
  useAthletesAndAnthropometryEntriesCursorConnectionQuery,
} from "../../../../types";

/** Larger pages = fewer round-trips; payload is slimmed in the GraphQL selection. */
export const ATHLETES_ANTHROPOMETRY_PAGE_SIZE = 100;

type AthleteEdge = AthletesAndAnthropometryEntriesCursorConnectionQuery["athletesCursorConnection"]["edges"][number];

type Options = {
  onError?: (error: ApolloError) => void;
};

/**
 * Loads all athletes + anthropometry entries in cursor pages.
 * Replaces the monolithic AllAthletesAndAnthropometryEntries query which times out on large orgs.
 */
export const useAllAthletesAndAnthropometryEntriesBatched = (options?: Options) => {
  const inFlightCursor = useRef<string | null>(null);
  const onErrorRef = useRef(options?.onError);
  onErrorRef.current = options?.onError;

  const [fetchMoreError, setFetchMoreError] = useState<ApolloError | undefined>();

  const { data, loading, error, fetchMore, networkStatus } = useAthletesAndAnthropometryEntriesCursorConnectionQuery({
    variables: {
      pagination: {
        cursor: null,
        limit: ATHLETES_ANTHROPOMETRY_PAGE_SIZE,
      },
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onError: options?.onError,
  });

  const pageInfo = data?.athletesCursorConnection.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage === true;
  const endCursor = pageInfo?.endCursor ?? null;
  const settledError = error ?? fetchMoreError;

  // Keep requesting the next page until the connection is exhausted (same pattern as useAllTeamworksOrgData).
  useEffect(() => {
    let cancelled = false;

    if (loading || settledError || !hasNextPage || !endCursor || inFlightCursor.current === endCursor) {
      return;
    }

    inFlightCursor.current = endCursor;
    void fetchMore({
      variables: {
        pagination: {
          cursor: endCursor,
          limit: ATHLETES_ANTHROPOMETRY_PAGE_SIZE,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }
        return {
          athletesCursorConnection: {
            ...fetchMoreResult.athletesCursorConnection,
            edges: [...prev.athletesCursorConnection.edges, ...fetchMoreResult.athletesCursorConnection.edges],
          },
        };
      },
    })
      .then(() => {
        if (cancelled) {
          return;
        }
        if (inFlightCursor.current === endCursor) {
          inFlightCursor.current = null;
        }
      })
      .catch((e: unknown) => {
        // Consume the rejection and settle into an error state — do not clear in a way that
        // lets the loading-dependent effect retry the same failed page.
        const apolloError =
          e instanceof ApolloError ? e : new ApolloError({ errorMessage: e instanceof Error ? e.message : String(e) });
        if (!cancelled) {
          setFetchMoreError(apolloError);
          onErrorRef.current?.(apolloError);
        }
        if (inFlightCursor.current === endCursor) {
          inFlightCursor.current = null;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [loading, settledError, hasNextPage, endCursor, fetchMore]);

  const athletes: readonly AthleteEdge[] | undefined = data?.athletesCursorConnection.edges;
  const fetchingMore = networkStatus === NetworkStatus.fetchMore;
  const done = Boolean(athletes) && !hasNextPage && !loading && !fetchingMore;

  return {
    data: done && !settledError && athletes ? { athletes } : undefined,
    loading: !settledError && !done,
    error: settledError ?? undefined,
  };
};
